import { z } from "zod";

/** The customer tags round (brief `docs/briefs/customer-tags-and-ticket-top.md`, "After the red team's review"): a tag is a ticket's label
 *  (kind "ticket", today's) or a person's (kind "customer": workspace-wide, without a project, shown on every ticket from them and copied onto
 *  none). Pure: the kinds, the one spelling of a name, the chip's words, the request bodies of the app's customer tag routes, and the subject
 *  rule of the ticket's top (the same round). */

export const TAG_KINDS = ["ticket", "customer"] as const;
export type TagKind = (typeof TAG_KINDS)[number];

/** The longest tag name (the API's rule) */
export const TAG_NAME_MAX = 40;

/** The one spelling of a tag name from any source: a person typing in the app, the connected AI, a WordPress site's role ("Gold Member",
 *  "shop_manager"): lower case, spaces and underscores to hyphens, anything else outside a-z, 0-9 and hyphens dropped, one hyphen at a time,
 *  none at either end, at most 40 characters. An empty result means "no name" (the caller skips it). */
export function normaliseTagName(text: string): string {
  const name = text.trim().toLowerCase().replace(/[\s_]+/g, "-").replace(/[^a-z0-9-]/g, "").replace(/-+/g, "-").replace(/^-+|-+$/g, "");
  return name.slice(0, TAG_NAME_MAX).replace(/-+$/, "");
}

/** The hover of a customer tag's chip on a ticket: it belongs to the person, not to this ticket */
export const customerTagHover = (name: string): string => `Tag on ${name}: every ticket from them shows it`;

/** A tag as a chip shows it (a customer tag on the person, the ticket read's `customer.tags`, a customer row's `tags`) */
export const TagChipSchema = z.object({ id: z.string(), name: z.string(), color: z.string().nullable() });
export type TagChip = z.infer<typeof TagChipSchema>;

/** `POST /customers/:id/tags`: customer tag ids to put on the person or take off; at least one */
export const CustomerTagsSetSchema = z.object({ add: z.array(z.string().min(1)).max(50).optional(), remove: z.array(z.string().min(1)).max(50).optional() }).strict()
  .refine((v) => (v.add?.length ?? 0) + (v.remove?.length ?? 0) > 0, { message: "Name a tag to add or to remove" });
export type CustomerTagsSet = z.infer<typeof CustomerTagsSetSchema>;

/** A WordPress site's word about a person's tags (the public API's two customer doors): at most 20 names a call, each spelled the one way;
 *  a name that spells to nothing is dropped */
export const SITE_TAGS_MAX = 20;
export const SiteTagsSchema = z.array(z.string().max(200)).max(SITE_TAGS_MAX).transform((names) => [...new Set(names.map(normaliseTagName).filter(Boolean))]);

/* ---------- the subject rule of the ticket's top ---------- */

/** a subject without what mail programs and we add: reply and forward prefixes (Re, Fwd, Fw, AW, WG, repeated, "Re[2]:" too), the ticket's
 *  tag "[#4117]" that every reply we send carries, letter case and extra spaces */
export function bareSubject(subject: string): string {
  let s = subject.replace(/\[#\d+\]/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
  const prefix = /^(re|fwd|fw|aw|wg)(\[\d+\])?\s*:\s*/;
  while (prefix.test(s)) s = s.replace(prefix, "");
  return s.trim();
}

/** Whether a later message shows its own subject: only when it differs from the one before (`previous`, the subject the thread last showed)
 *  once prefixes, our ticket tag, case and spacing are ignored; a message without a subject (a note, a call, an older message) shows none. */
export function showsOwnSubject(previous: string | null, current: string | null): boolean {
  if (!current) return false;
  const now = bareSubject(current);
  return now !== "" && now !== bareSubject(previous ?? "");
}
